import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MultiSelect } from '@/components/users/MultiSelect';
import { LwField } from '@/components/lw';
import { US_STATES } from './types';
import type { RecruitmentFormValues } from './validations';
import { UserRoleSelect } from './UserRoleSelect';

// Banked compensation options (0.75% to 3% in 0.25% increments)
const BANKED_COMPENSATION_OPTIONS = [
  { value: '0.75', label: '0.75%' },
  { value: '1.00', label: '1.00%' },
  { value: '1.25', label: '1.25%' },
  { value: '1.50', label: '1.50%' },
  { value: '1.75', label: '1.75%' },
  { value: '2.00', label: '2.00%' },
  { value: '2.25', label: '2.25%' },
  { value: '2.50', label: '2.50%' },
  { value: '2.75', label: '2.75%' },
  { value: '3.00', label: '3.00%' }
];

interface ProfessionalInfoSectionProps {
  form: UseFormReturn<RecruitmentFormValues>;
}

export const ProfessionalInfoSection: React.FC<ProfessionalInfoSectionProps> = ({ form }) => {
  const { register, formState: { errors }, watch, setValue } = form;

  const stateLicenses = watch('stateLicenses') || [];
  const bankedCompensation = watch('bankedCompensation');
  const position = watch('position');
  const mentorId = watch('mentorId');
  const brokerMentorCommissionSplit = watch('brokerMentorCommissionSplit');

  // Mentor split is required when mentor is selected
  const mentorSplitRequired = Boolean(mentorId);
  const mentorSplitMissing = mentorSplitRequired &&
    (brokerMentorCommissionSplit === undefined ||
     brokerMentorCommissionSplit === null ||
     brokerMentorCommissionSplit === '' as unknown);

  return (
    <div>
      <div className="dw-sec-title">Professional credentials</div>
      <div className="dw-grid">
        <LwField label={<>NMLS number <span className="req">*</span></>} hint="5–10 digit number" htmlFor="nmlsNumber" error={errors.nmlsNumber?.message}>
          <input id="nmlsNumber" className={errors.nmlsNumber ? 'invalid' : ''} {...register('nmlsNumber')} placeholder="123456" maxLength={10} />
        </LwField>
        <LwField label="DRE number" hint="8 digits (California only)" htmlFor="dreNumber" error={errors.dreNumber?.message}>
          <input id="dreNumber" className={errors.dreNumber ? 'invalid' : ''} {...register('dreNumber')} placeholder="01234567" maxLength={16} />
        </LwField>

        <LwField label="Years of experience" htmlFor="yearsOfExperience" error={errors.yearsOfExperience?.message}>
          <input id="yearsOfExperience" type="number" step="0.5" min="0" max="50" className={errors.yearsOfExperience ? 'invalid' : ''} {...register('yearsOfExperience')} placeholder="5" />
        </LwField>
        <LwField label={<>Position <span className="req">*</span></>} htmlFor="position" error={errors.position?.message}>
          <Select
            value={position || ''}
            onValueChange={(value) => setValue('position', value as 'Loan Officer' | 'Senior Loan Officer' | 'Branch Manager', { shouldValidate: true, shouldDirty: true })}
          >
            <SelectTrigger id="position">
              <SelectValue placeholder="Select position" />
            </SelectTrigger>
            <SelectContent className="lw-ds">
              <SelectItem value="Loan Officer">Loan Officer</SelectItem>
              <SelectItem value="Senior Loan Officer">Senior Loan Officer</SelectItem>
              <SelectItem value="Branch Manager">Branch Manager</SelectItem>
            </SelectContent>
          </Select>
        </LwField>

        <LwField
          span="full"
          label={<>State licenses <span className="req">*</span></>}
          htmlFor="stateLicenses"
          hint={stateLicenses.length > 0 ? `${stateLicenses.length} state${stateLicenses.length > 1 ? 's' : ''} selected` : 'Select at least one state'}
          error={errors.stateLicenses?.message}
        >
          <MultiSelect
            id="stateLicenses"
            options={US_STATES}
            value={stateLicenses}
            onChange={(newValue) => setValue('stateLicenses', newValue, { shouldValidate: true, shouldDirty: true })}
            placeholder="Select licensed states…"
          />
        </LwField>

        <LwField label="Banked compensation" hint="Compensation percentage (0.75% – 3%)" htmlFor="bankedCompensation" error={errors.bankedCompensation?.message}>
          <Select
            value={bankedCompensation !== undefined ? bankedCompensation.toFixed(2) : ''}
            onValueChange={(value) => {
              if (!value || value === '') {
                setValue('bankedCompensation', undefined, { shouldValidate: true, shouldDirty: true });
              } else {
                setValue('bankedCompensation', parseFloat(value), { shouldValidate: true, shouldDirty: true });
              }
            }}
          >
            <SelectTrigger id="bankedCompensation">
              <SelectValue placeholder="Select percentage" />
            </SelectTrigger>
            <SelectContent className="lw-ds">
              {BANKED_COMPENSATION_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </LwField>
        <LwField label={<>Broker commission split <span className="req">*</span></>} hint="Required field — maximum 85%" htmlFor="brokerCommissionSplit" error={errors.brokerCommissionSplit?.message}>
          <div className="field-affix has-r">
            <input id="brokerCommissionSplit" type="number" step="0.5" min="0" max="85" className={errors.brokerCommissionSplit ? 'invalid' : ''} {...register('brokerCommissionSplit')} placeholder="75" />
            <span className="affix r">%</span>
          </div>
        </LwField>

        <LwField label="Banked override (basis points)" hint="0–50 basis points (0% – 0.50%)" htmlFor="bankedOverrideBps" error={errors.bankedOverrideBps?.message}>
          <div className="field-affix has-r">
            <input id="bankedOverrideBps" type="number" step="0.01" min="0" max="50" className={errors.bankedOverrideBps ? 'invalid' : ''} {...register('bankedOverrideBps')} placeholder="25" />
            <span className="affix r">bps</span>
          </div>
        </LwField>
        <LwField label="Override recipient" htmlFor="overrideRecipient" error={errors.overrideRecipientId?.message}>
          <UserRoleSelect
            id="overrideRecipient"
            value={watch('overrideRecipientId') || ''}
            onChange={(value) => setValue('overrideRecipientId', value, { shouldValidate: true, shouldDirty: true })}
            error={errors.overrideRecipientId?.message}
            placeholder="Select recipient"
          />
        </LwField>

        <LwField
          label={<>Broker mentor split (%){mentorSplitRequired && <span className="req"> *</span>}</>}
          hint={mentorSplitRequired ? 'Required when mentor is selected (max 50%)' : 'Mentor split for first 5 brokered deals (max 50%)'}
          htmlFor="brokerMentorCommissionSplit"
          error={errors.brokerMentorCommissionSplit?.message}
        >
          <div className="field-affix has-r">
            <input id="brokerMentorCommissionSplit" type="number" step="0.5" min="0" max="50" className={errors.brokerMentorCommissionSplit || mentorSplitMissing ? 'invalid' : ''} {...register('brokerMentorCommissionSplit')} placeholder="50" />
            <span className="affix r">%</span>
          </div>
        </LwField>
        <LwField label="Mentor" htmlFor="mentor" error={errors.mentorId?.message}>
          <UserRoleSelect
            id="mentor"
            value={watch('mentorId') || ''}
            onChange={(value) => setValue('mentorId', value, { shouldValidate: true, shouldDirty: true })}
            error={errors.mentorId?.message}
            placeholder="Select mentor"
          />
        </LwField>

        <LwField label="Maximum compensation" hint="Maximum commission per deal ($10,000 – $999,999)" htmlFor="maximumCompensation" error={errors.maximumCompensation?.message}>
          <div className="field-affix has-l">
            <span className="affix l">$</span>
            {/*
              Deliberately NOT spread with register(): this input is fully
              controlled (own value + onChange -> setValue). Doing both gave RHF
              a DOM ref it re-synced _formValues from, so an empty field became
              '' while the baseline stayed undefined — isDirty then stuck true
              forever with an empty dirtyFields, which is the phantom
              "You have unsaved changes" prompt. Affects any recruit with no
              maximum compensation on file.
            */}
            <input
              id="maximumCompensation"
              type="text"
              maxLength={9}
              className={errors.maximumCompensation ? 'invalid' : ''}
              name="maximumCompensation"
              placeholder="50,000"
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                const limitedValue = value.slice(0, 6);
                const formattedValue = limitedValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                e.target.value = formattedValue;
                setValue('maximumCompensation', limitedValue ? parseInt(limitedValue, 10) : undefined, {
                  shouldValidate: true,
                  shouldDirty: true
                });
              }}
              value={
                watch('maximumCompensation')
                  ? watch('maximumCompensation')?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                  : ''
              }
            />
          </div>
        </LwField>
      </div>
    </div>
  );
};
